import { domainError, type DomainError } from '../shared/domain-error';
import { compareNames, normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import { asId, type ProjectId, type ServiceChecklistId, type ServiceId } from '../shared/ids';
import { QUALITY_ERROR, QUALITY_ERROR_MESSAGE } from './errors';

/**
 * A checklist de qualidade de um serviço — a FVS, Ficha de Verificação de Serviços
 * (Fase 19, fatia 1).
 *
 * ```text
 * FORM.109 · Execução de Alvenaria Não Estrutural · amostragem 40%
 * aplica-se a: ALVENARIA > ASSENTAMENTO DE ALVENARIA
 *
 * 1  Marcação da Primeira Fiada   Com o auxílio do prumo, nível e esquadro…   Prumo ±0,5cm…
 * 2  Espessura da Junta           Durante a elevação, verificar a uniformidade…
 * …
 * ```
 *
 * ## Ela aponta para o serviço por id (ADR-138, substitui a ADR-127)
 *
 * A ligação por nome normalizado existia porque não havia cadastro de serviço para apontar:
 * o serviço nascia por obra, e uma ficha global ligada por id precisaria de serviços globais
 * para todas as obras. A Fase 21 criou esse cadastro, então a ficha aponta para ele —
 * e renomear um serviço deixou de desfazer a ligação em silêncio.
 *
 * A ficha continua valendo para vários serviços sem ser copiada: `serviceIds` é lista, e é
 * por isso que `CABEAMENTO`, `PLACA + ACABAMENTO` e `INFRAESTRUTURA E ALIMENTAÇÃO` dividem a
 * mesma FVS.
 *
 * ## O que é catálogo e o que é documento
 *
 * Isto é catálogo: editar a ficha muda o que será perguntado **amanhã**. O que foi
 * respondido ontem mora na verificação, com o texto do item copiado (ADR-128) — a ficha
 * respondida é documento do dia, e não muda porque alguém corrigiu uma tolerância depois.
 */

export interface ChecklistItem {
  /** Estável dentro da checklist; é por ele que a resposta encontra o item. */
  readonly id: string;
  readonly position: number;
  /** "Item de Verificação" — o que se olha. */
  readonly label: string;
  /** "Método de Verificação" — como se olha. */
  readonly method?: string;
  /** "Tolerância" — o quanto pode desviar. Ausente é o `NA` da ficha. */
  readonly tolerance?: string;
}

export interface ServiceChecklist {
  readonly id: ServiceChecklistId;
  /** Ausente = checklist global do sistema, disponível a todas as obras (RN-007). */
  readonly projectId?: ProjectId;
  /** O código do formulário na construtora — `FORM.109`. Informativo. */
  readonly code?: string;
  readonly name: string;
  /** Os serviços que esta ficha verifica (ADR-138). Vazia = ficha que ainda não aponta. */
  readonly serviceIds: readonly ServiceId[];
  /** A amostragem que a ficha declara — 40%, 100%. Informativa: o sistema não sorteia. */
  readonly samplingPct?: number;
  readonly items: readonly ChecklistItem[];
  readonly position: number;
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export const MAX_CHECKLIST_ITEMS = 30;
export const MAX_CHECKLIST_NAME = 160;
export const MAX_CHECKLIST_CODE = 30;
export const MAX_CHECKLIST_SERVICES = 50;
export const MAX_ITEM_LABEL = 200;
export const MAX_ITEM_METHOD = 1000;
export const MAX_ITEM_TOLERANCE = 160;

const GLOBAL_SCOPE = '*';

/** Chave natural: única no escopo global ou dentro da obra — espelha a da disciplina. */
export function serviceChecklistNaturalKey(projectId: ProjectId | undefined, name: string): string {
  return `${projectId ?? GLOBAL_SCOPE}|${normalizeName(name)}`;
}

function refuse(code: string, details?: Record<string, unknown>): Result<never, DomainError> {
  return err(domainError(code, QUALITY_ERROR_MESSAGE[code]!, details));
}

/**
 * Valida os serviços que a ficha verifica (ADR-138).
 *
 * Id repetido entra uma vez: a tela marca e desmarca caixas, e marcar duas vezes o mesmo
 * serviço é acidente de interface, não erro de quem cadastrou. Texto em branco é descartado
 * pelo mesmo critério de antes — a tela mostra linhas para preencher, e quem deixou uma pela
 * metade não errou (RN-211).
 *
 * Lista vazia é ficha que ainda não aponta para serviço nenhum. É estado legítimo: a ficha
 * existe no catálogo e não é perguntada em medição nenhuma até alguém marcá-la. O que ela
 * **não** é mais é ficha órfã por acidente de grafia — isso a ligação por id encerrou.
 */
export function parseServiceIds(
  entradas: readonly string[],
): Result<readonly ServiceId[], DomainError> {
  const ids: ServiceId[] = [];
  const vistos = new Set<string>();

  for (const entrada of entradas) {
    const id = entrada.trim();
    if (id === '' || vistos.has(id)) continue;
    vistos.add(id);
    ids.push(asId<'Service'>(id));
  }

  if (ids.length > MAX_CHECKLIST_SERVICES) {
    return refuse(QUALITY_ERROR.CHECKLIST_TOO_MANY_SERVICES, {
      count: ids.length,
      max: MAX_CHECKLIST_SERVICES,
    });
  }
  return ok(ids);
}

/** O que a tela manda por item: o id só existe para quem já foi gravado. */
export interface ChecklistItemInput {
  readonly id?: string | undefined;
  readonly label: string;
  readonly method?: string | undefined;
  readonly tolerance?: string | undefined;
}

/**
 * Valida os itens de verificação (RN-223).
 *
 * Item sem rótulo é linha em branco e é descartado; checklist que fica sem item não é
 * gravada — uma ficha vazia não verifica nada, e guardá-la faria a medição exigir uma
 * resposta a pergunta nenhuma. As posições são renumeradas na ordem em que chegaram:
 * a ordem da ficha é a ordem da inspeção no canteiro.
 *
 * `nextId` é injetado porque o domínio não gera identificador — e o item novo precisa de
 * um, estável, para que a resposta de amanhã o encontre.
 */
export function parseChecklistItems(
  entradas: readonly ChecklistItemInput[],
  nextId: () => string,
): Result<readonly ChecklistItem[], DomainError> {
  const itens: ChecklistItem[] = [];

  for (const entrada of entradas) {
    const label = entrada.label.trim();
    if (label === '') continue;

    const method = entrada.method?.trim() ?? '';
    const tolerance = entrada.tolerance?.trim() ?? '';

    if (label.length > MAX_ITEM_LABEL) {
      return refuse(QUALITY_ERROR.ITEM_LABEL_TOO_LONG, { label: label.slice(0, 40) });
    }
    if (method.length > MAX_ITEM_METHOD) {
      return refuse(QUALITY_ERROR.ITEM_METHOD_TOO_LONG, { label });
    }
    if (tolerance.length > MAX_ITEM_TOLERANCE) {
      return refuse(QUALITY_ERROR.ITEM_TOLERANCE_TOO_LONG, { label });
    }

    itens.push({
      id: entrada.id === undefined || entrada.id.trim() === '' ? nextId() : entrada.id,
      position: itens.length,
      label,
      ...(method === '' ? {} : { method }),
      ...(tolerance === '' ? {} : { tolerance }),
    });
  }

  if (itens.length === 0) return refuse(QUALITY_ERROR.CHECKLIST_WITHOUT_ITEMS);
  if (itens.length > MAX_CHECKLIST_ITEMS) {
    return refuse(QUALITY_ERROR.CHECKLIST_TOO_MANY_ITEMS, {
      count: itens.length,
      max: MAX_CHECKLIST_ITEMS,
    });
  }
  return ok(itens);
}

function parseName(name: string): Result<string, DomainError> {
  const limpo = name.trim();
  if (limpo === '') return refuse(QUALITY_ERROR.CHECKLIST_NAME_REQUIRED);
  if (limpo.length > MAX_CHECKLIST_NAME) {
    return refuse(QUALITY_ERROR.CHECKLIST_NAME_TOO_LONG, { name: limpo.slice(0, 40) });
  }
  return ok(limpo);
}

function parseSampling(samplingPct: number | undefined): Result<number | undefined, DomainError> {
  if (samplingPct === undefined) return ok(undefined);
  if (!Number.isInteger(samplingPct) || samplingPct < 1 || samplingPct > 100) {
    return refuse(QUALITY_ERROR.CHECKLIST_SAMPLING_INVALID, { samplingPct });
  }
  return ok(samplingPct);
}

function parseCode(code: string | undefined): string | undefined {
  const limpo = code?.trim() ?? '';
  return limpo === '' ? undefined : limpo.slice(0, MAX_CHECKLIST_CODE);
}

export interface CreateServiceChecklistInput {
  readonly id: ServiceChecklistId;
  readonly projectId?: ProjectId;
  readonly code?: string;
  readonly name: string;
  readonly serviceIds: readonly ServiceId[];
  readonly samplingPct?: number;
  /** Já validados por `parseChecklistItems`. */
  readonly items: readonly ChecklistItem[];
  readonly position: number;
  readonly now: string;
}

export function createServiceChecklist(
  input: CreateServiceChecklistInput,
): Result<ServiceChecklist, DomainError> {
  const name = parseName(input.name);
  if (!name.ok) return err(name.error);
  const sampling = parseSampling(input.samplingPct);
  if (!sampling.ok) return err(sampling.error);
  const servicos = parseServiceIds(input.serviceIds);
  if (!servicos.ok) return err(servicos.error);
  if (input.items.length === 0) return refuse(QUALITY_ERROR.CHECKLIST_WITHOUT_ITEMS);

  const code = parseCode(input.code);

  return ok({
    id: input.id,
    name: name.value,
    serviceIds: servicos.value,
    items: input.items,
    position: input.position,
    isActive: true,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.projectId === undefined ? {} : { projectId: input.projectId }),
    ...(code === undefined ? {} : { code }),
    ...(sampling.value === undefined ? {} : { samplingPct: sampling.value }),
  });
}

export interface EditServiceChecklistInput {
  readonly code?: string;
  readonly name: string;
  readonly serviceIds: readonly ServiceId[];
  readonly samplingPct?: number;
  readonly items: readonly ChecklistItem[];
}

/**
 * Edita a checklist já cadastrada.
 *
 * A identidade é o **nome** — trocá-lo reescreve a chave natural, e por isso o caso de
 * uso reverifica a unicidade antes de gravar. `undefined` remove o opcional: quem apaga o
 * código na tela quer a ficha sem código, não o código anterior de volta (espelha
 * `editDiscipline`).
 */
export function editServiceChecklist(
  current: ServiceChecklist,
  input: EditServiceChecklistInput,
  now: string,
): Result<ServiceChecklist, DomainError> {
  const name = parseName(input.name);
  if (!name.ok) return err(name.error);
  const sampling = parseSampling(input.samplingPct);
  if (!sampling.ok) return err(sampling.error);
  const servicos = parseServiceIds(input.serviceIds);
  if (!servicos.ok) return err(servicos.error);
  if (input.items.length === 0) return refuse(QUALITY_ERROR.CHECKLIST_WITHOUT_ITEMS);

  const code = parseCode(input.code);
  const { code: _code, samplingPct: _sampling, ...rest } = current;

  return ok({
    ...rest,
    name: name.value,
    serviceIds: servicos.value,
    items: input.items,
    updatedAt: now,
    ...(code === undefined ? {} : { code }),
    ...(sampling.value === undefined ? {} : { samplingPct: sampling.value }),
  });
}

/**
 * Liga e desliga a checklist.
 *
 * Desligar não é excluir: as verificações já feitas continuam existindo, com o texto que
 * copiaram. O que a ficha desativada deixa de fazer é ser perguntada na próxima medição.
 */
export function setServiceChecklistActive(
  checklist: ServiceChecklist,
  isActive: boolean,
  now: string,
): ServiceChecklist {
  return { ...checklist, isActive, updatedAt: now };
}

export function checklistNameTakenError(name: string): DomainError {
  return domainError(
    QUALITY_ERROR.CHECKLIST_NAME_TAKEN,
    QUALITY_ERROR_MESSAGE[QUALITY_ERROR.CHECKLIST_NAME_TAKEN]!,
    { name },
  );
}

/** A checklist está disponível para esta obra? Globais estão disponíveis para todas (RN-007). */
export function isChecklistAvailableForProject(
  checklist: ServiceChecklist,
  projectId: ProjectId,
): boolean {
  return checklist.projectId === undefined || checklist.projectId === projectId;
}

export function compareChecklists(a: ServiceChecklist, b: ServiceChecklist): number {
  return a.position - b.position || compareNames(a.name, b.name);
}

/** A checklist verifica este serviço? Por id, desde a Fase 21 (ADR-138). */
export function checklistMatchesService(
  checklist: Pick<ServiceChecklist, 'serviceIds'>,
  serviceId: ServiceId,
): boolean {
  return checklist.serviceIds.includes(serviceId);
}

/**
 * As checklists **ativas** que valem para o serviço, na ordem do catálogo.
 *
 * Só as ativas, porque é esta a lista que a medição pergunta: uma ficha desativada não
 * pode virar exigência (RN-218) nem opção.
 */
export function checklistsForService(
  checklists: readonly ServiceChecklist[],
  serviceId: ServiceId,
): readonly ServiceChecklist[] {
  return checklists
    .filter((item) => item.isActive && checklistMatchesService(item, serviceId))
    .sort(compareChecklists);
}

/** `FORM.109 — Execução de Alvenaria Não Estrutural`, ou só o nome quando não há código. */
export function describeChecklist(checklist: Pick<ServiceChecklist, 'code' | 'name'>): string {
  return checklist.code === undefined ? checklist.name : `${checklist.code} — ${checklist.name}`;
}
